import { useMemo } from 'react'

import {
  SubEventType,
  SubEvents,
  Sessions,
  Participants,
  OptionsParticipants,
  OptionPathKeys,
  OptionType,
  OptionsBase,
  Rooms,
  Events,
} from '../../../types'
import { useLocationDataOptions, useRoomsDataOptions } from '../../../utils'

export type ParticipantOptions = OptionsParticipants & {
  subEventName: string
  start: string
  end: string
  subEventType?: SubEventType
  rooms?: ReturnType<typeof useRoomsDataOptions>
  location?: number | null
  eventLocation?: number | null
}

export const useFilterParticipantOptions = (
  participantsDetails: Participants | null | undefined,
  subEvents: SubEvents[] | undefined,
  sessions: Sessions[] | undefined,
  event: Events
): Record<OptionPathKeys, ParticipantOptions[]> => {
  const locationsOptions = useLocationDataOptions()
  const roomsOptions = useRoomsDataOptions(event.location, locationsOptions)

  const participantOptions: Record<OptionPathKeys, ParticipantOptions[]> = useMemo(() => {
    const sessionOptions = [] as ParticipantOptions[]
    const mealOptions = [] as ParticipantOptions[]
    const hotelOptions = [] as ParticipantOptions[]
    const workshopOptions = [] as ParticipantOptions[]
    const subEventOptions = [] as ParticipantOptions[]
    const textOptions = [] as ParticipantOptions[]
    const numberOptions = [] as ParticipantOptions[]
    const checkboxOptions = [] as ParticipantOptions[]

    participantsDetails?.participant_options.forEach(elem => {
      const findSubEvent = subEvents?.find(
        subEl => subEl.id === (elem.option as OptionsBase).sub_event
      )
      const findSession = sessions?.find(
        session => session.id === (elem.option as OptionsBase).session
      )
      const selectedDetails = findSubEvent ?? findSession

      const opts = {
        ...elem,
        subEventType: findSubEvent?.sub_event_type,
        subEventName: selectedDetails?.name ?? 'NC',
        start: selectedDetails?.start ?? '',
        end: selectedDetails?.end ?? '',
        location:
          selectedDetails && 'location' in selectedDetails ? selectedDetails.location : null,
        eventLocation: event.location,
        rooms:
          selectedDetails && 'rooms' in selectedDetails && selectedDetails?.rooms
            ? (selectedDetails?.rooms
                .map(room => roomsOptions.find(el => el.id === room))
                .filter(el => el !== undefined) ?? ([] satisfies Rooms[]))
            : [],
      } satisfies ParticipantOptions

      if (findSubEvent) {
        switch (findSubEvent?.sub_event_type) {
          case SubEventType.MealSubEvent:
            mealOptions.push(opts)
            break
          case SubEventType.HotelSubEvent:
            hotelOptions.push(opts)
            break
          case SubEventType.WorkShopSubEvent:
            workshopOptions.push(opts)
            break
          default:
            subEventOptions.push(opts)
            break
        }
      } else if (findSession) {
        sessionOptions.push(opts)
      } else {
        switch (elem.option.type) {
          case OptionType.TextOption:
            textOptions.push(opts)
            break
          case OptionType.NumberOption:
            numberOptions.push(opts)
            break
          case OptionType.CheckBoxOption:
            checkboxOptions.push(opts)
            break
          default:
            // Fallback
            subEventOptions.push(opts)
            break
        }
      }
    })

    return {
      sessionOptions,
      mealOptions,
      hotelOptions,
      workshopOptions,
      subEventOptions,
      textOptions,
      numberOptions,
      checkboxOptions,
    }
  }, [participantsDetails?.participant_options, subEvents, sessions, event.location, roomsOptions])

  return participantOptions
}
